import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  LightBulbIcon,
  DocumentArrowDownIcon,
  ClipboardIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { RiskBadge } from "./RiskBadge";
import type {
  AnalysisResultsProps,
  CollapsibleSectionProps,
} from "../types/component.types";
import { toast } from "sonner";

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  analysis,
  onDownloadReport,
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["summary", "risks", "recommendations"])
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success(t("common.copiedToClipboard") || "Copiado al portapapeles");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      low: "text-atlantis-600",
      medium: "text-yellow-600",
      high: "text-orange-600",
      critical: "text-red-600",
    };
    return colors[severity] || "text-tiber-700";
  };

  const getSeverityBg = (severity: string) => {
    const colors: Record<string, string> = {
      low: "bg-atlantis-50",
      medium: "bg-yellow-50",
      high: "bg-orange-50",
      critical: "bg-red-50",
    };
    return colors[severity] || "bg-romance-50";
  };

  return (
    <div className="space-y-6">
      {/* Overall Risk and Actions */}
      <div className="bg-white rounded-lg border border-tiber-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-juridico mb-2">
              {t("documentReview.overallRisk")}
            </h3>
            <RiskBadge risk={analysis.overall_risk} size="lg" />
            {analysis.confidence_score && (
              <p className="text-sm text-tiber-700 mt-2">
                {t("documentReview.confidenceScore")}:{" "}
                {analysis.confidence_score}%
              </p>
            )}
          </div>
          {onDownloadReport && (
            <button
              onClick={onDownloadReport}
              className="flex items-center gap-2 px-4 py-2 bg-tiber-600 text-white rounded-lg hover:bg-tiber-700 transition-colors shadow-sm"
            >
              <DocumentArrowDownIcon className="h-5 w-5" />
              {t("documentReview.downloadReport")}
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <CollapsibleSection
        title={t("documentReview.sections.summary")}
        isExpanded={expandedSections.has("summary")}
        onToggle={() => toggleSection("summary")}
        icon={<ExclamationCircleIcon className="h-5 w-5" />}
      >
        <div className="relative group">
          <p className="text-tiber-700 font-light leading-relaxed whitespace-pre-line pr-10">
            {analysis.summary}
          </p>
          <button
            onClick={() => copyToClipboard(analysis.summary, "summary")}
            className="absolute top-0 right-0 p-2 text-titan hover:text-tiber-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title={t("common.copy")}
          >
            {copiedId === "summary" ? (
              <CheckIcon className="h-5 w-5 text-atlantis-600" />
            ) : (
              <ClipboardIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </CollapsibleSection>

      {/* Risks */}
      {analysis.risks.length > 0 && (
        <CollapsibleSection
          title={t("documentReview.sections.risks")}
          count={analysis.risks.length}
          isExpanded={expandedSections.has("risks")}
          onToggle={() => toggleSection("risks")}
          icon={<ExclamationTriangleIcon className="h-5 w-5 text-red-600" />}
        >
          <div className="space-y-4">
            {analysis.risks.map((risk, index) => {
              const riskId = `risk-${index}`;
              const fullText = `${risk.description}\n${risk.recommendation ? `${t("documentReview.recommendation")}: ${risk.recommendation}` : ""}`;

              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border transition-all hover:shadow-sm group relative ${getSeverityBg(risk.severity)}`}
                >
                  <button
                    onClick={() => copyToClipboard(fullText, riskId)}
                    className="absolute top-4 right-4 p-1.5 text-titan hover:text-tiber-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t("common.copy")}
                  >
                    {copiedId === riskId ? (
                      <CheckIcon className="h-4 w-4 text-atlantis-600" />
                    ) : (
                      <ClipboardIcon className="h-4 w-4" />
                    )}
                  </button>

                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon
                      className={`h-5 w-5 shrink-0 mt-0.5 ${getSeverityColor(risk.severity)}`}
                    />
                    <div className="flex-1 pr-8">
                      <div className="flex items-center gap-2 mb-2">
                        <RiskBadge risk={risk.severity} size="sm" />
                        {risk.location && (
                          <span className="text-xs text-tiber-700">
                            {risk.location}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-light text-juridico mb-2">
                        {risk.description}
                      </p>
                      {risk.recommendation && (
                        <div className="mt-2 p-3 bg-white/50 rounded border border-tiber-200/50 backdrop-blur-sm">
                          <p className="text-xs font-medium text-tiber-700 mb-1">
                            {t("documentReview.recommendation")}:
                          </p>
                          <p className="text-sm font-light text-tiber-700">
                            {risk.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Omissions */}
      {analysis.omissions.length > 0 && (
        <CollapsibleSection
          title={t("documentReview.sections.omissions")}
          count={analysis.omissions.length}
          isExpanded={expandedSections.has("omissions")}
          onToggle={() => toggleSection("omissions")}
          icon={<ExclamationCircleIcon className="h-5 w-5 text-yellow-600" />}
        >
          <div className="space-y-3">
            {analysis.omissions.map((omission, index) => {
              const omissionId = `omission-${index}`;
              const fullText = `${omission.description}\n${t("documentReview.shouldInclude")}: ${omission.recommendation}`;

              return (
                <div
                  key={index}
                  className="p-4 rounded-lg border border-tiber-200 bg-yellow-50 group relative transition-all hover:shadow-sm"
                >
                  <button
                    onClick={() => copyToClipboard(fullText, omissionId)}
                    className="absolute top-4 right-4 p-1.5 text-titan hover:text-tiber-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t("common.copy")}
                  >
                    {copiedId === omissionId ? (
                      <CheckIcon className="h-4 w-4 text-atlantis-600" />
                    ) : (
                      <ClipboardIcon className="h-4 w-4" />
                    )}
                  </button>

                  <div className="flex items-start gap-3">
                    <div className="shrink-0 pr-1">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                          omission.importance === "high"
                            ? "bg-red-100 text-red-800"
                            : omission.importance === "medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-tiber-100 text-tiber-800"
                        }`}
                      >
                        {t(`documentReview.importance.${omission.importance}`)}
                      </span>
                    </div>
                    <div className="flex-1 pr-8">
                      <p className="text-sm text-juridico mb-2">
                        {omission.description}
                      </p>
                      <div className="mt-2 p-3 bg-white/50 rounded border border-tiber-200/50 backdrop-blur-sm">
                        <p className="text-xs font-medium text-tiber-700 mb-1">
                          {t("documentReview.shouldInclude")}:
                        </p>
                        <p className="text-sm font-light text-tiber-700">
                          {omission.recommendation}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Sensitive Clauses */}
      {analysis.sensitive_clauses.length > 0 && (
        <CollapsibleSection
          title={t("documentReview.sections.sensitiveClauses")}
          count={analysis.sensitive_clauses.length}
          isExpanded={expandedSections.has("clauses")}
          onToggle={() => toggleSection("clauses")}
          icon={<ExclamationCircleIcon className="h-5 w-5 text-orange-600" />}
        >
          <div className="space-y-4">
            {analysis.sensitive_clauses.map((clause, index) => {
              const clauseId = `clause-${index}`;
              const fullText = `${t("documentReview.clause")}: ${clause.clause}\n${t("documentReview.concern")}: ${clause.concern}\n${t("documentReview.suggestion")}: ${clause.suggestion}`;

              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border group relative transition-all hover:shadow-sm ${getSeverityBg(clause.severity)}`}
                >
                  <button
                    onClick={() => copyToClipboard(fullText, clauseId)}
                    className="absolute top-4 right-4 p-1.5 text-titan hover:text-tiber-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t("common.copy")}
                  >
                    {copiedId === clauseId ? (
                      <CheckIcon className="h-4 w-4 text-atlantis-600" />
                    ) : (
                      <ClipboardIcon className="h-4 w-4" />
                    )}
                  </button>

                  <div className="flex items-start gap-2 mb-2">
                    <RiskBadge risk={clause.severity} size="sm" />
                  </div>
                  <div className="mb-3 p-3 bg-white/50 rounded border border-tiber-200/50 backdrop-blur-sm">
                    <p className="text-xs font-medium text-tiber-700 mb-1">
                      {t("documentReview.clause")}:
                    </p>
                    <p className="text-sm text-juridico italic">
                      "{clause.clause}"
                    </p>
                  </div>
                  <div className="mb-2 pr-8">
                    <p className="text-xs font-medium text-tiber-700 mb-1">
                      {t("documentReview.concern")}:
                    </p>
                    <p className="text-sm text-tiber-700">{clause.concern}</p>
                  </div>
                  <div className="mt-2 p-3 bg-white/50 rounded border border-tiber-200/50 backdrop-blur-sm pr-8">
                    <p className="text-xs font-medium text-tiber-700 mb-1">
                      {t("documentReview.suggestion")}:
                    </p>
                    <p className="text-sm font-light text-tiber-700">
                      {clause.suggestion}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CollapsibleSection>
      )}

      {/* Recommendations */}
      {analysis.recommendations.length > 0 && (
        <CollapsibleSection
          title={t("documentReview.sections.recommendations")}
          count={analysis.recommendations.length}
          isExpanded={expandedSections.has("recommendations")}
          onToggle={() => toggleSection("recommendations")}
          icon={<LightBulbIcon className="h-5 w-5 text-tiber-600" />}
        >
          <ul className="space-y-3">
            {analysis.recommendations.map((recommendation, index) => {
              const recId = `rec-${index}`;
              return (
                <li
                  key={index}
                  className="group relative flex items-center gap-3 p-2 rounded-lg hover:bg-romance-50 transition-colors"
                >
                  <div className="shrink-0 mt-1">
                    <div className="h-6 w-6 rounded-full bg-tiber-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-tiber-700">
                        {index + 1}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm font-light text-tiber-700 flex-1 pr-10">
                    {recommendation}
                  </p>
                  <button
                    onClick={() => copyToClipboard(recommendation, recId)}
                    className="absolute top-2 right-2 p-1 text-titan hover:text-tiber-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t("common.copy")}
                  >
                    {copiedId === recId ? (
                      <CheckIcon className="h-4 w-4 text-atlantis-600" />
                    ) : (
                      <ClipboardIcon className="h-4 w-4" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </CollapsibleSection>
      )}
    </div>
  );
};

// Collapsible Section Component
interface CollapsibleSectionPropsInternal extends CollapsibleSectionProps {
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionPropsInternal> = ({
  title,
  count,
  isExpanded,
  onToggle,
  icon,
  children,
}) => {
  return (
    <div className="bg-white rounded-lg border border-tiber-200 overflow-hidden shadow-xs">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-romance-50 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="transition-transform group-hover:scale-110 duration-200">
            {icon}
          </div>
          <h3 className="text-base font-bold text-juridico group-hover:text-tiber-700 transition-colors">
            {title}
            {count !== undefined && (
              <span className="ml-2 text-sm font-normal text-titan">
                ({count})
              </span>
            )}
          </h3>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-5 w-5 text-titan group-hover:text-tiber-500 transition-colors" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-titan group-hover:text-tiber-500 transition-colors" />
        )}
      </button>
      {isExpanded && (
        <div className="px-6 py-4 border-t border-tiber-200 animate-in fade-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};
